import { fetchJsonWithTimeout } from "./fetch-json-timeout.js";

export const DEFAULT_SUBMIT_TIMEOUT_MS = 30_000;

export function isServerSuccess(res, data) {
  if (!res?.ok || !data || typeof data !== "object") {
    return false;
  }
  if (data.ok !== true) {
    return false;
  }
  return data.status === "accepted" || data.status === "already_registered";
}

export function successMessage(data) {
  if (data?.status === "already_registered") {
    return data.preview
      ? "Este e-mail já consta na pré-visualização local."
      : "Este e-mail já está na lista.";
  }
  if (data?.status === "accepted") {
    return data.preview
      ? "Cadastro registrado em modo pré-visualização (sem envio de e-mail)."
      : "Obrigado! Você está na lista.";
  }
  return "";
}

/**
 * @param {Response} res
 * @param {object | null} data
 */
export function mapSubmitFailure(res, data) {
  if (res.status === 429) {
    const wait = data?.retry_after ? ` Aguarde ${data.retry_after}s.` : "";
    return {
      message: `Muitas tentativas.${wait}`,
      retryable: true,
      field: null,
    };
  }

  if (!res.ok) {
    let msg = data?.message || "Não foi possível concluir o cadastro. Tente novamente.";
    if (res.status === 403 || res.status === 415) {
      msg = "Não foi possível enviar agora. Atualize a página e tente novamente.";
    } else if (res.status === 409) {
      msg =
        data?.message ||
        "Não foi possível confirmar seu cadastro. Fale conosco pelo Instagram @alabastra.perfume.";
    } else if (res.status === 413) {
      msg = "Os dados enviados são grandes demais. Tente novamente.";
    } else if (res.status >= 500) {
      msg = "Serviço temporariamente indisponível. Tente novamente em instantes.";
    }

    let field = null;
    if (data?.error === "email_invalid" || data?.error === "email_required") {
      field = "email";
      msg = "Informe um e-mail válido.";
    }

    return {
      message: msg,
      retryable: data?.retryable !== false,
      field,
    };
  }

  if (!isServerSuccess(res, data)) {
    return {
      message: "Não foi possível concluir o cadastro. Tente novamente.",
      retryable: true,
      field: null,
    };
  }

  return null;
}

/**
 * @param {typeof fetch} fetchFn
 * @param {object} payload
 * @param {{ timeoutMs?: number }} options
 */
export async function postWaitlistPayload(fetchFn, payload, options = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_SUBMIT_TIMEOUT_MS;
  const { res, data } = await fetchJsonWithTimeout(
    fetchFn,
    "/api/waitlist",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    },
    timeoutMs,
  );

  const failure = mapSubmitFailure(res, data);
  if (failure) {
    return { ok: false, ...failure, data, status: res.status };
  }

  return { ok: true, data, status: res.status };
}

export function createSubmitController(fetchFn = fetch, options = {}) {
  let inFlight = false;
  let lastPayload = null;

  async function submit(payload, handlers = {}) {
    if (inFlight) {
      return { ok: false, skipped: true };
    }
    inFlight = true;
    lastPayload = payload;
    handlers.onPending?.("Enviando…");

    try {
      const result = await postWaitlistPayload(fetchFn, payload, options);
      if (result.ok) {
        handlers.onSuccess?.(successMessage(result.data), result.data);
      } else if (!result.skipped) {
        handlers.onError?.(result.message, {
          retryable: result.retryable,
          field: result.field,
        });
      }
      return result;
    } catch (err) {
      const aborted = err?.name === "AbortError";
      handlers.onError?.(
        aborted
          ? "A solicitação demorou demais. Tente novamente."
          : "Falha de conexão. Verifique a rede e tente novamente.",
        { retryable: true, field: null },
      );
      return { ok: false, retryable: true };
    } finally {
      inFlight = false;
    }
  }

  function retry(handlers) {
    if (!lastPayload) {
      return Promise.resolve({ ok: false, skipped: true });
    }
    return submit(lastPayload, handlers);
  }

  function getLastPayload() {
    return lastPayload;
  }

  function isInFlight() {
    return inFlight;
  }

  return { submit, retry, getLastPayload, isInFlight };
}
